import { faRotateLeft } from '@fortawesome/pro-light-svg-icons'
import { CompanyTypeOfCustomersTyInversed } from '@teinor/erp/types/company/config/parameter'
import { CustomerModelType } from '@teinor/erp/types/company/customer'
import { useCallback, useMemo, useState } from 'react'
import { TabItems, SwitchButton } from '../../../baseComponents/Button/SwitchButton'
import { permissionCheck } from '../../../baseComponents/PermissionChecker/function'
import { TwinTableLink } from '../../../baseComponents/TwinTable'
import { TableComponentProps } from '../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderString, CustomRenderBoolean, EditRow, DeleteRow } from '../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import TwinTrans from '../../../baseComponents/TwinTrans'
import { TwinDictionary } from '../../../utils/globals/dictionary'
import useCustomerTypesDict from '../../../utils/hooks/customer/useCustomerTypesDict'
import useEditDeleteModal from '../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { getConfigParam, getAllClientManagerEmployees, getSingleEmployeeParam } from '../../../utils/reducers/getters'
import ModalChangeActiveCustomer from '../ModalChangeActiveCustomerCustomer'
import { TableInstanceType } from '../../../baseComponents/TwinTable/types'
import { twinFetchPostJSON } from '../../../utils/globals/data'
import { changeEmployeeParams } from '../../../utils/reducers/reduxDispatch'
import { EmployeesPayload } from '../../../utils/reducers/company/employees'
import { OnRowClick } from '../../../baseComponents/TwinTable/VirtualTableListing/types'
import locations from '../../../utils/globals/locations'

type TableAllCustomersProps = TableComponentProps & {
    employees: EmployeesPayload
    onRowClick?: OnRowClick
}

const TableAllCustomers: React.FC<TableAllCustomersProps> = ({ setTableInstance, employees, userPermissions, tableInstance, onRowClick}) => {
    const { t } = useTwinTranslation()
    const { openModal, setOpenModal } = useEditDeleteModal()
    const myEmployees = useMemo(() => getConfigParam('showOnlyClientManagerEmployees') === 'on' ? getAllClientManagerEmployees() || {} : employees || {}, [employees])
    const { filterCustomers, changeFilterCustomer } = useTableAllCustomersLogic({ tableInstance })
    const { dictCustomerTypeFilter } = useCustomerTypesDict(filterCustomers)
    const allCustomers = useMemo(() => {
        const companyTypeOfCustomers = getConfigParam('companyTypeOfCustomers')
        const parsedCTypeOfCustomers = parseInt(String(companyTypeOfCustomers))
        return parsedCTypeOfCustomers === CompanyTypeOfCustomersTyInversed['all']
    }, [])
    const specialColumns: ColumnTableSchema<CustomerModelType> = []
    if (filterCustomers !== 1) {
        specialColumns.push({
            id: 'fiscal_name',
            dataKey: 'fiscal_name',
            label: t('fiscalName', 'Nombre Fiscal'),
            minWidth: 170,
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('fiscalName', 'Nombre Fiscal') }
            }
        })
    }
    if (filterCustomers !== 2) {
        specialColumns.push({
            id: 'surname',
            dataKey: 'surname',
            label: t('surname', 'Apellidos'),
            sortable: true,
            minWidth: 130,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('surname', 'Apellidos') }
            },
            searchKey: 'Customer.surname',
        })
    }
    const columns: ColumnTableSchema<CustomerModelType> = [
        {
            id: 'code',
            dataKey: 'code',
            label: t('code', 'Código'),
            minWidth: 90,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customerCode', 'Código de cliente') }
            },
            searchKey: 'Customer.code'
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            minWidth: 120,
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            },
            searchKey: 'Customer.name'
        },
        ...specialColumns,
        {
            id: 'customer_type',
            dataKey: 'customer_type',
            label: t('customerType', 'Tipo de cliente'),
            minWidth: 140,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: dictCustomerTypeFilter, label: t('customerType', 'Tipo de cliente') }
            },
            customRender: (value) => <CustomRenderCustomerType value={value} />,
        },
        {
            id: 'email',
            dataKey: 'email',
            label: t('email', 'Email'),
            minWidth: 110,
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            },
            searchKey: 'Customer.email'
        },
        {
            id: 'phone',
            dataKey: 'phone',
            label: t('phone', 'Teléfono'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('phone', 'Teléfono') }
            },
            searchKey: 'Customer.phone'
        },
        {
            id: 'mobile',
            dataKey: 'mobile',
            label: t('mobile', 'Móvil'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('mobile', 'Móvil') }
            },
            searchKey: 'Customer.mobile'
        },
        {
            id: 'CustomerOriginId',
            dataKey: 'CustomerOriginId',
            label: t('origin', 'Origen'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerOrigin?.name} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('origin', 'Origen') }
            },
            searchKey: 'CustomerOrigin.name',
            hideColumnByDefault: true
        },
        {
            id: 'ResponsibleEmployeeId',
            dataKey: 'ResponsibleEmployeeId',
            label: t('ourManager', 'Nuestro gestor'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.ResponsibleEmployee?.fullname_short} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: myEmployees, label: t('ourManager', 'Nuestro gestor'), fieldName: 'fullname_short' }
            },
            searchSingleKey: 'ResponsibleEmployeeId',
            searchKey: 'fullname_short',
            sortable: true,
            hideColumnByDefault: true
        },
        {
            id: 'active',
            dataKey: 'active',
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: {
                    items: {
                        'true': { name: t('actived', 'Activados') },
                        'false': { name: t('disabled', 'Desactivados') },
                        'all': { name: t('all', 'Todos') }
                    }, label: t('active', 'Activo')
                }
            },
            searchKey: 'Customer.active',
            generalSearchable: false
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpenModal({ type: 'delete', allRowData })} icon={!allRowData?.active ? faRotateLeft : undefined} messageTooltip={!allRowData?.active ? t('reactivate', 'Reactivar') : undefined} />,
            havePermission: permissionCheck(userPermissions, 'delete')
        }
    ]
    const items: TabItems[] = [
        {
            element: <TwinTrans transKey={'all'}>Todos</TwinTrans>
        },
        {
            element: <TwinTrans transKey={'individuals'}>Particulares</TwinTrans>
        },
        {
            element: <TwinTrans transKey={'companies'}>Empresas</TwinTrans>
        },
    ]
    let extraWhereSubstituibleParams: TwinDictionary = {}
    if (filterCustomers === 2) {
        extraWhereSubstituibleParams = {
            ...extraWhereSubstituibleParams,
            'customer_type': { '$in': [1, 2] }
        }
    } else if (filterCustomers === 1) {
        extraWhereSubstituibleParams = {
            ...extraWhereSubstituibleParams,
            'customer_type': { '$in': [0, 3] }
        }
    }
    return (
        <TwinTableLink className={allCustomers ? 'table_all_customers' : ''} createRowDataLink={[locations.allCustomers, ['$id', 'main']]} name={'allCustomers_' + filterCustomers} columns={columns} getDataFrom='/api/app/customer/getAllCustomers' setTableInstance={setTableInstance} extraWhereSubstituibleParams={extraWhereSubstituibleParams} onRowClick={onRowClick}>
            {allCustomers ? <SwitchButton className='mr-auto' onClick={changeFilterCustomer} active={filterCustomers} items={items} /> : null}
            {openModal?.type === 'delete' ? <ModalChangeActiveCustomer {...{ setOpened: () => setOpenModal(null) }} allRowData={openModal.allRowData} onSubmit={() => tableInstance?.getTableDataFromStart()} /> : null}
        </TwinTableLink>
    )
}


interface TableAllCustomersLogicProps {
    tableInstance: TableInstanceType
}
const useTableAllCustomersLogic = ({ tableInstance }: TableAllCustomersLogicProps) => {
    const [filterCustomers, setFilterCustomers] = useState<number>(() => {
        const allCustomers = parseInt(String(getConfigParam('companyTypeOfCustomers'))) === 0
        if (allCustomers && getSingleEmployeeParam('table_allCustomers_selector')) {
            return parseInt(getSingleEmployeeParam('table_allCustomers_selector') || '0') + 1
        }
        return 0
    })

    const changeFilterCustomer = useCallback(async (val: number) => {
        setFilterCustomers(val)
        await tableInstance?.getTableDataFromStart?.(true)
        await twinFetchPostJSON('/api/app/config/employee/updateEmployeeParameter', { parameterKey: 'table_allCustomers_selector', value: val - 1 })
        changeEmployeeParams({ 'table_allCustomers_selector': val - 1 })
    }, [setFilterCustomers, tableInstance])

    return { filterCustomers, changeFilterCustomer }
}


interface CustomRenderCustomerTypeProps {
    value: string
}

const CustomRenderCustomerType = ({ value }: CustomRenderCustomerTypeProps) => {
    const { dictCustomerTypeComplete } = useCustomerTypesDict()
    const parsedValue = parseInt(value)
    return (
        <div className={'flex items-center justify-center border rounded-5 px-10 py-4 text-center '} style={{ color: dictCustomerTypeComplete?.[parsedValue]?.colorText }}>
            {dictCustomerTypeComplete?.[parsedValue]?.name}
        </div>
    )
}

export default TableAllCustomers
